/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { groupByInstallPackage } from '../groupByInstallPackage/groupByInstallPackage';
import { getTranslationImports } from '../translations/translations';
import { getPluginsImport } from '../getPluginsImport/getPluginsImport';
import { getUsersIntegrationPlugin } from '../getUsersIntegrationPlugin/getUsersIntegrationPlugin';
import { getCommentsIntegrationPlugin } from '../getCommentsIntegrationPlugin/getCommentsIntegrationPlugin';
import { getRevisionHistoryIntegrationPlugin } from '../getRevisionHistoryIntegrationPlugin/getRevisionHistoryIntegrationPlugin';
import { getTrackChangesIntegrationPlugin } from '../getTrackChangesIntegrationPlugin/getTrackChangesIntegrationPlugin';
import { getConfigUpdateAlert } from '../getConfigUpdateAlert/getConfigUpdateAlert';
import { getTokenVariables } from '../getTokenVariables/getTokenVariables';
import { getOnReady } from './getOnReady';
import { getViewChildRefs } from './getViewChildRefs';
import type { GetAngularSourceTemplateArgs } from './types';
import { getBuilderUrlComment } from '../getBuilderUrlComment/getBuilderUrlComment';
import { getDefaultHexColors } from '../getDefaultHexColors/getDefaultHexColors';

export function getAngularSourceTemplateNpm( {
	customerPortalAccount,
	editorConfig,
	editorOptions,
	evaluationLicense,
	selectedInstallMethod,
	builderUrl
}: GetAngularSourceTemplateArgs ): string {
	const { editorPlugins, toolbarOptions, editorType, editorLanguage } = editorOptions;

	const groupedPlugins = groupByInstallPackage( editorPlugins );
	const hasPremiumPlugins = groupedPlugins[ 'ckeditor5-premium-features' ].length > 0;
	const viewChildRefs = getViewChildRefs( editorPlugins, toolbarOptions, editorType );
	const configUpdateAlert = getConfigUpdateAlert( {
		editorPlugins,
		editorConfig,
		configRef: 'this.config',
		lang: 'ts'
	} );
	const { importsFromCKEditor, importsFromCKEditorPremiumFeatures } = getPluginsImport( editorType, groupedPlugins );

	return `
		${ getBuilderUrlComment( builderUrl ) }

		${ getAngularImports( !!viewChildRefs ) }
		import { CommonModule } from '@angular/common';
		import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
		import { type EditorConfig,${ importsFromCKEditor } } from 'ckeditor5';
		${ hasPremiumPlugins ? `import { ${ importsFromCKEditorPremiumFeatures } } from 'ckeditor5-premium-features';` : '' }

		${ getTranslationImports( editorLanguage, hasPremiumPlugins ) }

		${ getTokenVariables( { editorConfig, editorPlugins, customerPortalAccount, evaluationLicense, selectedInstallMethod } ) }

		${ getDefaultHexColors( editorPlugins ) }

		${ getUsersIntegrationPlugin( editorPlugins ) }

		${ getCommentsIntegrationPlugin( editorPlugins ) }

		${ getTrackChangesIntegrationPlugin( editorPlugins ) }

		${ getRevisionHistoryIntegrationPlugin( editorPlugins ) }

		@Component( {
			selector: 'app-root',
			standalone: true,
			imports: [ CommonModule, CKEditorModule ],
			templateUrl: './app.component.html',
			styleUrls: [ './app.component.css' ],
			encapsulation: ViewEncapsulation.None
		} )
		export class AppComponent implements AfterViewInit {
			${ viewChildRefs }

			constructor( private changeDetector: ChangeDetectorRef ) {}

			public isLayoutReady = false;
			public Editor = ${ editorType };
			public config: EditorConfig = {}; // CKEditor needs the DOM tree before calculating the configuration.
			public ngAfterViewInit(): void {
				this.config = ${ editorConfig }

				${ configUpdateAlert.call }

				this.isLayoutReady = true;
				this.changeDetector.detectChanges();
			}

			${ getOnReady( editorType, toolbarOptions, editorPlugins ) }
		};

		${ configUpdateAlert.fn }
	`;
}

function getAngularImports( shouldIncludeRefImports: boolean ) {
	const imports = [
		'ChangeDetectorRef',
		'Component',
		'ViewEncapsulation',
		'type AfterViewInit',
		shouldIncludeRefImports ? 'ElementRef' : '',
		shouldIncludeRefImports ? 'ViewChild' : ''
	].filter( Boolean ).join( ', ' );

	return `import { ${ imports } } from '@angular/core';`;
}
